import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Printer, Check, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatTime, relativeMinutes } from "@/lib/format";

/**
 * Kitchen "Printer" view.
 *
 * Renders each incoming order as a thermal-style ticket. Tickets list in
 * arrival order. The kitchen marks each ticket "printed" once paper is in hand,
 * which removes it from the queue. Item statuses are still tracked behind the
 * scenes (server marks all items "preparing" when printed) so customers still
 * see meaningful progress on their app.
 */
export default function PrinterFeed({ restaurantId }) {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [printingId, setPrintingId] = useState(null);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const url = restaurantId
                ? `/kitchen/print-queue?restaurant_id=${restaurantId}`
                : "/kitchen/print-queue";
            const { data } = await api.get(url);
            setTickets(data || []);
        } catch (error) {
            console.warn("[printer-feed] load failed", error?.message);
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        load();
        const id = setInterval(load, 4000);
        return () => clearInterval(id);
    }, [load]);

    const markPrinted = async (order) => {
        setPrintingId(order.id);
        try {
            await api.post(`/kitchen/orders/${order.id}/printed`);
            // Auto-advance all received items to "preparing" so customers see progress.
            await Promise.all(
                (order.items || [])
                    .filter((it) => it.status === "received")
                    .map((it) =>
                        api
                            .patch(`/orders/${order.id}/items/${it.id}/status`, { status: "preparing" })
                            .catch((e) => console.warn("[printer-feed] advance item failed", e?.message))
                    )
            );
            toast.success(`Ticket for Table ${order.table?.label || "—"} printed`);
            load();
        } catch (error) {
            console.warn("[printer-feed] markPrinted failed", error?.message);
            toast.error("Could not mark printed");
        } finally {
            setPrintingId(null);
        }
    };

    if (loading) return <Skeleton className="h-72 w-full" />;

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-muted-foreground inline-flex items-center gap-2">
                    <Printer className="h-4 w-4" />
                    {tickets.length === 0
                        ? "No tickets waiting to print"
                        : `${tickets.length} ticket${tickets.length === 1 ? "" : "s"} ready to print`}
                </div>
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <Loader2 className={`h-3 w-3 ${refreshing ? "animate-spin" : "opacity-30"}`} /> auto-refresh
                </div>
            </div>

            {tickets.length === 0 ? (
                <Card>
                    <CardContent className="p-10 text-center">
                        <Printer className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="font-medium">All clear</p>
                        <p className="text-sm text-muted-foreground">New orders will print here automatically.</p>
                    </CardContent>
                </Card>
            ) : (
                <ScrollArea className="h-[75vh] pr-2">
                    <div className="flex flex-wrap gap-3" data-testid="printer-feed">
                        {tickets.map((order) => (
                            <PrinterTicket
                                key={order.id}
                                order={order}
                                printing={printingId === order.id}
                                onPrinted={() => markPrinted(order)}
                            />
                        ))}
                    </div>
                </ScrollArea>
            )}
        </div>
    );
}

/**
 * A receipt-style ticket. Uses a monospaced font, dashed borders, and a torn-paper
 * bottom edge to evoke a real kitchen printer.
 */
function PrinterTicket({ order, printing, onPrinted }) {
    const tableLabel = order.table?.label || "—";
    const itemsCount = order.items?.length || 0;
    return (
        <Card
            className="bg-[hsl(var(--brand-cream))] border-dashed border-2 border-foreground/30 shadow-md w-[280px] flex flex-col"
            data-testid="printer-ticket"
        >
            <CardContent className="p-4 font-mono text-[13px] leading-snug">
                <div className="text-center border-b border-dashed border-foreground/40 pb-2 mb-2">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        {order.restaurant_name || "Kitchen"}
                    </div>
                    <div className="font-bold text-lg">TABLE {tableLabel}</div>
                    <div className="text-[10px] text-muted-foreground inline-flex items-center justify-center gap-1">
                        <Clock className="h-3 w-3" /> {formatTime(order.created_at)} · {relativeMinutes(order.created_at)}
                    </div>
                </div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                    Guest: {order.customer?.name || "—"}
                </div>
                <div className="space-y-1.5 mb-3">
                    {order.items?.map((it) => (
                        <div key={it.id} className="flex justify-between gap-2">
                            <div>
                                <div>
                                    <span className="font-bold">{it.quantity}×</span> {it.name}
                                </div>
                                {it.notes && (
                                    <div className="text-[11px] text-muted-foreground italic pl-3">&gt; {it.notes}</div>
                                )}
                            </div>
                            <div className="text-muted-foreground text-[11px] uppercase">{it.status.slice(0, 4)}</div>
                        </div>
                    ))}
                </div>
                <div className="border-t border-dashed border-foreground/40 pt-2 text-center">
                    <div className="text-[10px] text-muted-foreground tracking-wider">
                        {itemsCount} item{itemsCount === 1 ? "" : "s"} · ID {order.id.slice(0, 6)}
                    </div>
                </div>
            </CardContent>
            <div className="px-3 pb-3">
                <Button
                    size="sm"
                    className="w-full"
                    onClick={onPrinted}
                    disabled={printing}
                    data-testid="printer-ticket-mark-printed"
                >
                    {printing ? (
                        <>
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Printing…
                        </>
                    ) : (
                        <>
                            <Check className="h-3.5 w-3.5 mr-1" /> Mark printed
                        </>
                    )}
                </Button>
            </div>
            {/* Torn paper edge */}
            <div
                className="h-2 -mt-1"
                aria-hidden
                style={{
                    backgroundImage:
                        "linear-gradient(45deg, transparent 33%, hsl(var(--brand-cream)) 33%, hsl(var(--brand-cream)) 66%, transparent 66%)",
                    backgroundSize: "12px 100%",
                }}
            />
        </Card>
    );
}

PrinterFeed.displayName = "PrinterFeed";
